import {Language, supportedLanguages} from './language'

export function groupBy<T, TGroup extends string | number | symbol>(
  values: T[],
  keyFinder: string | ((value: T) => TGroup)
): Record<TGroup, T[]> {
  return values.reduce((groups, value) => {
    const key = typeof keyFinder === 'function' ? keyFinder(value) : (value[keyFinder] as TGroup)

    if (!groups[key]) {
      groups[key] = [value]
    } else {
      groups[key] = [...groups[key], value]
    }

    return groups
  }, {} as Record<TGroup, T[]>)
}

export type ArrayElementOf<T> = T extends (infer TElement)[]
  ? TElement
  : T extends readonly (infer TElement)[]
  ? TElement
  : never

export type ArgumentOf<T> = T extends (arg: infer TArgument) => any ? TArgument : never

export function comparePlantFields(x, y): boolean {
  if (Array.isArray(x) && Array.isArray(y)) {
    if (x.length !== y.length) {
      return false
    }
    const xSet = new Set(x.map((e) => (typeof e === 'string' ? e.toLowerCase() : e)))

    return y.every((element) =>
      xSet.has(typeof element === 'string' ? element.toLowerCase() : element)
    )
  }
  return x === y
}

export function cached<T>(fn: () => Promise<T>) {
  let pendingPromise: Promise<T> | null = null

  const functionWithCache = async () => {
    if (functionWithCache._cachedValue != null) {
      return functionWithCache._cachedValue
    }

    if (pendingPromise === null) {
      pendingPromise = fn()
    }

    const result = await pendingPromise

    functionWithCache._cachedValue = result
    pendingPromise = null

    return result
  }

  functionWithCache._cachedValue = null

  functionWithCache.clearCache = () => {
    pendingPromise = null
    functionWithCache._cachedValue = null
  }

  return functionWithCache
}

export function cachedById<T, TKey extends number | string = number>(
  fn: (id: TKey, language?: Language) => Promise<T>
) {
  type CacheKey = TKey | `${TKey}-${Language}`

  let pendingPromises = new Map<CacheKey, Promise<T>>()
  let cachedValues = new Map<CacheKey, T>()

  const functionWithCache = async (id: TKey, language?: Language) => {
    const cacheKey: CacheKey = language ? `${id}-${language}` : id

    if (cachedValues.has(cacheKey)) {
      // has() guarantees a value; Map.get cannot express that
      return cachedValues.get(cacheKey) as T
    }

    if (!pendingPromises.has(cacheKey)) {
      pendingPromises.set(cacheKey, fn(id, language))
    }

    const result = (await pendingPromises.get(cacheKey)) as T
    cachedValues.set(cacheKey, result)
    pendingPromises.delete(cacheKey)

    return result
  }

  functionWithCache.clearCachedById = (id: TKey) => {
    pendingPromises.delete(id)
    for (const language of supportedLanguages) {
      pendingPromises.delete(`${id}-${language}`)
    }

    cachedValues.delete(id)
    for (const language of supportedLanguages) {
      cachedValues.delete(`${id}-${language}`)
    }
  }

  functionWithCache.clearAllCached = () => {
    pendingPromises.clear()
    cachedValues.clear()
  }

  return functionWithCache
}
